import { Alert, Box, BoxProps, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useState } from 'react';
import { Trans } from 'react-i18next';

import { UploadButton } from '@/components/Form/UploadButton';
import { DesktopOnly, MobileOnly } from '@/components/Layout/Responsive';

import {
  MAX_DOCUMENT_SIZE,
  SUPPORTED_DOCUMENT_EXTENSIONS,
} from '@/constants/document';

import { useTranslate } from '@/hooks/useTranslate';

import { getFileExtension } from '@/utils/file';

import { Icon } from '../../../Icon/Icon';
import { IconCell } from '../../../Icon/IconCell';

import { FileSelectorContainer } from './components.styled';

type FileSelectorProps = {
  onSetFile: (value: File) => void;
} & BoxProps;

export function FileSelector({ onSetFile, ...props }: FileSelectorProps) {
  const { t, language } = useTranslate();
  const [fileError, setFileError] = useState('');

  const onFileSelect = async (files: FileList | null) => {
    setFileError('');

    if (!files || !files.length) return;

    const file = files[0];
    const ext = getFileExtension(file);

    if (!SUPPORTED_DOCUMENT_EXTENSIONS.includes(ext)) {
      setFileError(t('moreInfo.fileSelector.error.notSupported'));
      return;
    }

    if (file.size > MAX_DOCUMENT_SIZE) {
      setFileError(t('moreInfo.fileSelector.error.sizeExceeded'));
      return;
    }

    onSetFile(files[0]);
  };

  return (
    <FileSelectorContainer {...props}>
      <IconCell
        name="upload-alt"
        color={(theme: Theme) => theme.palette.primaries[100]}
        bgColor={(theme: Theme) => theme.palette.primaries[400]}
        size={79}
        iconSize={48}
      />
      <MobileOnly>
        <Typography variant="h5" mt={3} mb={1} textAlign="center">
          {t('moreInfo.fileSelector.titleMobile')}
        </Typography>
      </MobileOnly>
      <DesktopOnly>
        <Typography variant="body2" textAlign="center" mt={3} mb={1}>
          <Trans
            lang={language}
            i18nKey="moreInfo.fileSelector.titleDesktop"
            components={{
              b: <b />,
            }}
          />
        </Typography>
      </DesktopOnly>
      <Typography variant="caption" mb={8} textAlign="center">
        {t('moreInfo.fileSelector.description', {
          supportedFiles: SUPPORTED_DOCUMENT_EXTENSIONS.join(', '),
        })}
      </Typography>
      {fileError && (
        <Box mb={2}>
          <Alert icon={<Icon name="alert" />} color="error" variant="standard">
            {fileError}
          </Alert>
        </Box>
      )}
      <UploadButton
        onChange={onFileSelect}
        accept={SUPPORTED_DOCUMENT_EXTENSIONS.join(',')}
      >
        {t('moreInfo.fileSelector.button.upload')}
      </UploadButton>
    </FileSelectorContainer>
  );
}
